'use client';

import { useEffect, useState } from 'react';

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
const IDLE_TIMEOUT_MS = 4000;
const FALLBACK_DELAY_MS = 2000;

/**
 * Signals `true` once the browser reports idle or the user first interacts
 * with the page, whichever comes first. Used to keep always-mounted client
 * bundles (chat, feedback, cookie banner, analytics) out of the critical
 * rendering path — design D5 #3 (sdd/seo-audit-remediation), where these
 * account for the 276 KiB of unused JS measured on the homepage.
 */
export function useDeferredMount(): boolean {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (ready) return;

        const markReady = () => setReady(true);
        const hasIdleCallback = typeof window.requestIdleCallback === 'function';

        const idleId = hasIdleCallback
            ? window.requestIdleCallback(markReady, { timeout: IDLE_TIMEOUT_MS })
            : window.setTimeout(markReady, FALLBACK_DELAY_MS);

        INTERACTION_EVENTS.forEach((event) =>
            window.addEventListener(event, markReady, { once: true, passive: true })
        );

        return () => {
            if (hasIdleCallback) {
                window.cancelIdleCallback(idleId as number);
            } else {
                window.clearTimeout(idleId as number);
            }
            INTERACTION_EVENTS.forEach((event) => window.removeEventListener(event, markReady));
        };
    }, [ready]);

    return ready;
}
